import {
    type FC,
    useRef
} from "react";
import {
    View
} from "react-native";
import type ITreeViewGroupProps from "./type";
import stylesheet, {
    getTreeViewGroupGuide,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useTreeViewSelector,
    useTreeViewContext
} from "../../context";
import type {
    TreeViewEntryType
} from "../../type";
import {
    TREE_VIEW_PLACEHOLDER_COUNT
} from "../../util";
import TreeViewPlaceholder from "../placeholder";

const TreeViewGroup: FC<ITreeViewGroupProps> = ({
    renderItem,
    entry
}) => {
    const {
        isGuideVisible,
        selectionMode,
        customTheme,
        measures
    } = useTreeViewContext();

    const {
        borders,
        colors
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        container: containerDynamicStyle
    } = useStyles({
        measures
    });

    const lastChildrenRef = useRef<Array<TreeViewEntryType>>([]);

    const liveChildren = useTreeViewSelector((snapshot) => snapshot.childrenByParent.get(entry.key));

    if(liveChildren) {
        lastChildrenRef.current = liveChildren;
    }

    const children = liveChildren ?? lastChildrenRef.current;

    const isActive = useTreeViewSelector((snapshot) => selectionMode === "single" && children.some((child) => snapshot.selectedKeys.has(child.key)));

    const renderGuide = () => {
        if(!isGuideVisible) {
            return null;
        }

        return <View
            style={[
                stylesheet.guide,
                getTreeViewGroupGuide({
                    level: entry.level,
                    isActive,
                    measures,
                    borders,
                    colors
                })
            ]}
            aria-hidden={true}
        />;
    };

    const renderContent = () => {
        if(children.length) {
            return children.map(renderItem);
        }

        if(entry.isLoading) {
            return <TreeViewPlaceholder
                count={TREE_VIEW_PLACEHOLDER_COUNT}
                level={entry.level + 1}
                kind="skeleton"
            />;
        }

        return <TreeViewPlaceholder
            level={entry.level + 1}
            kind="note"
        />;
    };

    return <View
        aria-busy={entry.isLoading}
        style={[
            stylesheet.container,
            containerDynamicStyle
        ]}
        role="group"
    >
        {renderGuide()}
        {renderContent()}
    </View>;
};
export default TreeViewGroup;
